import React, { ReactNode } from 'react';
import { Table, Tag, Input } from 'antd';
import { DataRow, MatchType } from 'types/documentGroup';
import { COLOR_FAILURE, COLOR_SUCCESS, COLOR_WARNING } from 'app-constants';
import MatchStatusIcon from './MatchStatusIcon';
import Styled from '../drawers/drawers.styled';

const {
  StyledMatchModalButton,
  StyledMatchModalButtonsWrapper,
  StyledMatchModalConfirmationWrapper,
} = Styled;

interface OwnProps {
  columns: string[];
  rows: DataRow[];
  onAcceptRow: (acceptedRowIndex: number) => void;
  onRejectRow: (rejectedRowIndex: number) => void;
  onRejectReasonAdd: (rejectedRowIndex: number, rejectReason: string) => void;
}

interface TableColumn {
  title: string;
  dataIndex: string;
  key: string;
  render: (text: string, record: DataRow, index: number) => ReactNode;
}

const GroupListMatchForm = ({
  columns,
  rows,
  onAcceptRow,
  onRejectRow,
  onRejectReasonAdd,
}: OwnProps) => {
  function renderRow(columnIndex: number, record: DataRow): ReactNode {
    const currentRow = record.rowValues[columnIndex];
    let color;
    switch (currentRow.matchType) {
      case MatchType.FullMatch:
        color = COLOR_SUCCESS;
        break;
      case MatchType.PartialMatch:
        color = COLOR_WARNING;
        break;
      case MatchType.NoMatch:
        color = COLOR_FAILURE;
        break;
      default:
        color = '';
        break;
    }

    return <Tag color={color}>{currentRow.value}</Tag>;
  }

  const renderMatchStatusRow = (columnIndex: number, record: DataRow): ReactNode => {
    const currentRow = record.rowValues[columnIndex];
    return <MatchStatusIcon matchType={currentRow.matchType} />;
  };

  const renderButtonRow = (columnIndex: number, record: DataRow, rowIndex: number): ReactNode => {
    const currentRowColumn = record.rowValues[columnIndex];
    const rejectedReason = record.rowValues[columnIndex + 1];
    return (
      <StyledMatchModalConfirmationWrapper>
        {currentRowColumn.matchType !== MatchType.FullMatch && !rejectedReason && (
          <StyledMatchModalButtonsWrapper>
            <StyledMatchModalButton type='primary' onClick={() => onAcceptRow(rowIndex)}>
              <span>Accept</span>
            </StyledMatchModalButton>
            <StyledMatchModalButton type='primary' onClick={() => onRejectRow(rowIndex)}>
              <span>Reject</span>
            </StyledMatchModalButton>
          </StyledMatchModalButtonsWrapper>
        )}
        {rejectedReason && rejectedReason.value === '' && (
          <Input
            placeholder='Reject reason'
            onBlur={(e) => onRejectReasonAdd(rowIndex, e.target.value)}
          />
        )}
        {rejectedReason && rejectedReason.value !== '' && <span>{rejectedReason.value}</span>}
      </StyledMatchModalConfirmationWrapper>
    );
  };

  const mapColumns = (columnName: string, columnIndex: number): TableColumn => {
    let mappedColumnName = columnName;
    if (columnName === 'Unknown') {
      mappedColumnName = 'ID document';
    }

    const mappedColumn: TableColumn = {
      dataIndex: mappedColumnName,
      key: mappedColumnName,
      title: mappedColumnName,
      render: (_, record) => renderRow(columnIndex, record),
    };

    return mappedColumn;
  };

  const mapStaticColumns = (columnIndex: number): TableColumn[] => {
    const matchStatus: TableColumn = {
      dataIndex: 'matchStatus',
      key: 'matchStatus',
      title: 'Status',
      render: (_, record) => renderMatchStatusRow(columnIndex, record),
    };

    const buttons: TableColumn = {
      dataIndex: 'buttons',
      key: 'buttons',
      title: '',
      render: (_, record, rowIndex) => renderButtonRow(columnIndex, record, rowIndex),
    };

    return [matchStatus, buttons];
  };

  const mappedColumns = columns.map(mapColumns);
  const staticColumns = mapStaticColumns(columns.length);

  const allColumns = mappedColumns.concat(staticColumns);

  return <Table pagination={false} columns={allColumns} dataSource={rows} />;
};

export default GroupListMatchForm;
